import {
	AccessoryCountText,
	AccountNumberText,
	BillToTitle,
	CabinetCountText,
	CubeCountText,
	DesignerNumberText,
	DoorStyleText,
	EstimatedSubTotalText,
	FinishText,
	JobNameText,
	LineItemsInOrderText,
	OrderDetailsText,
	PONumberText,
	ReplacementReviewShippingHeader,
	ReplacementShipToDeliveryContactSubheaderText,
	ReplacementsShipToFormShippingLabelNotes,
	ReviewTruckStopOrderText,
	TruckOrderErrorDuplicatePO,
	TruckOrderErrorNeedPO,
	TruckOrderLineItemsError,
	UnavailableDataPlaceholderText
} from "constants/text";
import {
	AccordionDetailsWrapper,
	AccordionSummaryWrapper,
	AccordionWrapper,
	AccountAndBillTo,
	AccountAndBillToInfo,
	AccountAndBillToInfoContent,
	DetailsSummaryCardLogo,
	EstimatedSubTotal,
	EstimatedSubTotalBanner,
	EstimatedSubTotalBannerContainer,
	EstimatedSubTotalBannerLabel,
	EstimatedSubTotalBannerNote,
	EstimatedSubTotalBannerTextContainer,
	EstimatedSubTotalBannerValue,
	EstimatedSubTotalLabel,
	EstimatedSubTotalValue,
	Info,
	InfoContent,
	InfoTitle,
	OrderCardWrapper,
	OrderInfo,
	OrderInfoItemLabel,
	OrderInfoItemValue,
	OrderInfoItemWrapper,
	OrderListLabel,
	OrderListLineItems,
	OrderListLineItemsLabel,
	ProductDetails,
	ProductDetailsSubtitle,
	ProductDetailsTitle,
	ShippingAndBilling
} from "./ReviewTruckStopOrderCard.styles";
import { models } from "types/api/viewModels";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { brandCheck } from "utils/order";
import ReviewTruckLineItemCard from "../ReviewTruckLineItemCard/ReviewTruckLineItemCard";
import { formatPrice } from "utils/string";
import { NewOrderViewPriceEstimatePromotionalPricingText } from "components/NewOrders/constants";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import { TruckOrderError } from "../../TruckStyles.styles.ts";

interface ReviewTruckStopOrderCardProps {
	configuration: models["OrderConfigurationViewModel"];
	address?: models["AddressViewModel"];
	shipToAddress?: models["AddressViewModel"];
	poNumber: string | null | undefined;
	designerNumber: string | null | undefined;
	jobName: string | null | undefined;
	labelComments: string | null | undefined;
	truckOrderError?: number;
}

const ReviewTruckStopOrderCard = ({
	configuration,
	address,
	shipToAddress,
	poNumber,
	designerNumber,
	jobName,
	labelComments,
	truckOrderError
}: ReviewTruckStopOrderCardProps) => {
	const totalCabinetCount =
		configuration?.lineItems?.reduce((sum, item) => sum + (item.cabinetCount ?? 0), 0) ??
		UnavailableDataPlaceholderText;

	const totalAccessoryCount =
		configuration.lineItems?.reduce((sum, item) => sum + (item.accessoryCount ?? 0), 0) ??
		UnavailableDataPlaceholderText;

	const totalCubeCount =
		configuration.lineItems?.reduce((sum, item) => sum + (item.cubeCount ?? 0), 0)?.toFixed(2) ??
		UnavailableDataPlaceholderText;

	const billToId = configuration.billToId ?? UnavailableDataPlaceholderText;
	const brandCode = configuration.globals?.brand?.code ?? UnavailableDataPlaceholderText;
	const styleDescription = configuration.globals?.style?.description ?? UnavailableDataPlaceholderText;
	const speciesDescription = configuration.globals?.species?.description ?? UnavailableDataPlaceholderText;
	const finishDescription = configuration.globals?.finish?.description ?? UnavailableDataPlaceholderText;
	const shapeDescription = configuration.globals?.shape?.description ?? UnavailableDataPlaceholderText;
	const constructionDescription = configuration.globals?.construction?.description ?? UnavailableDataPlaceholderText;

	const shipToName = address?.name ?? UnavailableDataPlaceholderText;
	const shipToLine1 = address?.line1 ?? UnavailableDataPlaceholderText;
	const shipToLine2 = address?.line2;
	const shipToState = address?.state ?? UnavailableDataPlaceholderText;
	const shipToZip = address?.zip ?? UnavailableDataPlaceholderText;
	const shipToPhone = shipToAddress?.phoneNumber ?? UnavailableDataPlaceholderText;
	const shipToEmail = shipToAddress?.email ?? UnavailableDataPlaceholderText;

	const lineItemsCount = configuration.lineItems?.length ?? "0";

	return (
		<OrderCardWrapper
			elevation={1}
			square
			data-testid="order-card-wrapper"
		>
			{truckOrderError && (
				<TruckOrderError>
					<ErrorOutline />
					<span data-testid="truck-order-error-message">
						{truckOrderError === 40007 && TruckOrderErrorNeedPO}
						{truckOrderError === 40008 && TruckOrderLineItemsError}
						{(truckOrderError === 40009 || truckOrderError === 400) && TruckOrderErrorDuplicatePO}
					</span>
				</TruckOrderError>
			)}
			<OrderInfo data-testid="order-info">
				<OrderInfoItemWrapper>
					<OrderInfoItemLabel>{PONumberText}</OrderInfoItemLabel>
					<OrderInfoItemValue data-testid="order-info-item-po-number">
						{poNumber ?? UnavailableDataPlaceholderText}
					</OrderInfoItemValue>
				</OrderInfoItemWrapper>
				<OrderInfoItemWrapper>
					<OrderInfoItemLabel>{DoorStyleText}</OrderInfoItemLabel>
					<OrderInfoItemValue data-testid="order-info-item-door-style">{styleDescription}</OrderInfoItemValue>
				</OrderInfoItemWrapper>
				<OrderInfoItemWrapper>
					<OrderInfoItemLabel>{FinishText}</OrderInfoItemLabel>
					<OrderInfoItemValue data-testid="order-info-item-finish">{finishDescription}</OrderInfoItemValue>
				</OrderInfoItemWrapper>
				<OrderInfoItemWrapper>
					<OrderInfoItemLabel>{CabinetCountText}</OrderInfoItemLabel>
					<OrderInfoItemValue data-testid="order-info-item-cabinet-count">
						{totalCabinetCount}
					</OrderInfoItemValue>
				</OrderInfoItemWrapper>
				<OrderInfoItemWrapper>
					<OrderInfoItemLabel>{AccessoryCountText}</OrderInfoItemLabel>
					<OrderInfoItemValue data-testid="order-info-item-accessory-count">
						{totalAccessoryCount}
					</OrderInfoItemValue>
				</OrderInfoItemWrapper>
				<OrderInfoItemWrapper>
					<OrderInfoItemLabel>{CubeCountText}</OrderInfoItemLabel>
					<OrderInfoItemValue data-testid="order-info-item-cube-count">{totalCubeCount}</OrderInfoItemValue>
				</OrderInfoItemWrapper>
			</OrderInfo>
			<ShippingAndBilling data-testid="shipping-and-billing">
				<Info data-testid="order-details-info">
					<InfoTitle>{OrderDetailsText}</InfoTitle>
					<InfoContent>
						<OrderInfoItemWrapper>
							<OrderInfoItemLabel>{DesignerNumberText}</OrderInfoItemLabel>
							<OrderInfoItemValue data-testid="order-info-item-designer-number">
								{designerNumber ?? UnavailableDataPlaceholderText}
							</OrderInfoItemValue>
						</OrderInfoItemWrapper>
						<OrderInfoItemWrapper>
							<OrderInfoItemLabel>{JobNameText}</OrderInfoItemLabel>
							<OrderInfoItemValue data-testid="order-info-item-job-name">
								{jobName && jobName !== "" ? jobName : UnavailableDataPlaceholderText}
							</OrderInfoItemValue>
						</OrderInfoItemWrapper>
					</InfoContent>
				</Info>
				<Info data-testid="shipping-info">
					<InfoTitle>{ReplacementReviewShippingHeader}</InfoTitle>
					<InfoContent>
						<OrderInfoItemWrapper data-testid="order-info-item-ship-to-contact">
							<OrderInfoItemLabel>{ReplacementShipToDeliveryContactSubheaderText}</OrderInfoItemLabel>
							<OrderInfoItemValue data-testid="order-info-item-ship-to-phone">
								{shipToPhone}
							</OrderInfoItemValue>
							<OrderInfoItemValue data-testid="order-info-item-ship-to-email">
								{shipToEmail}
							</OrderInfoItemValue>
						</OrderInfoItemWrapper>
						<OrderInfoItemWrapper data-testid="order-info-item-ship-label-notes">
							<OrderInfoItemLabel data-testid="order-info-item-ship-label-notes">
								{ReplacementsShipToFormShippingLabelNotes}
							</OrderInfoItemLabel>
							<OrderInfoItemValue data-testid="order-info-item-ship-label-value">
								{labelComments && labelComments !== "" ? labelComments : UnavailableDataPlaceholderText}
							</OrderInfoItemValue>
						</OrderInfoItemWrapper>
					</InfoContent>
				</Info>
			</ShippingAndBilling>
			<AccordionWrapper>
				<AccordionSummaryWrapper expandIcon={<ExpandMoreIcon />}>
					<OrderListLabel>{ReviewTruckStopOrderText}</OrderListLabel>
					<OrderListLineItems data-testid="order-list-line-items">
						<OrderListLineItemsLabel>
							{lineItemsCount} {LineItemsInOrderText}
						</OrderListLineItemsLabel>
					</OrderListLineItems>
				</AccordionSummaryWrapper>
				<AccordionDetailsWrapper>
					<AccountAndBillTo data-testid="account-and-bill-to">
						<AccountAndBillToInfo data-testid="account-info">
							<InfoTitle>{AccountNumberText}</InfoTitle>
							<AccountAndBillToInfoContent>
								<OrderInfoItemWrapper data-testid="account-info-item">
									<OrderInfoItemValue>
										{configuration?.accountId ?? UnavailableDataPlaceholderText}
									</OrderInfoItemValue>
									{shipToName}, {shipToLine1}, {shipToState}
								</OrderInfoItemWrapper>
								<DetailsSummaryCardLogo
									data-testid="brand-logo"
									parentBrand={brandCode ?? UnavailableDataPlaceholderText}
									src={
										brandCheck(brandCode)
											? `/assets/manufacture_logos/${brandCode}.png`
											: "/assets/tandem_logos/cwg_logo.png"
									}
									alt={brandCheck(brandCode) ? String(brandCode) : "CabinetworksGroup Logo"}
								/>
							</AccountAndBillToInfoContent>
						</AccountAndBillToInfo>
						<AccountAndBillToInfo data-testid="bill-to-info">
							<InfoTitle>{BillToTitle}</InfoTitle>
							<InfoContent>
								<OrderInfoItemWrapper data-testid="bill-to-info-item">
									<OrderInfoItemValue data-testid="bill-to-info-item-value">
										{billToId} - {shipToName}
									</OrderInfoItemValue>
									<OrderInfoItemValue data-testid="bill-to-info-item-value-2">
										{shipToLine1}, {shipToLine2 && `${shipToLine2},`} {shipToState} {shipToZip}
									</OrderInfoItemValue>
								</OrderInfoItemWrapper>
							</InfoContent>
						</AccountAndBillToInfo>
					</AccountAndBillTo>
					<ProductDetails data-testid="product-details">
						<ProductDetailsTitle data-testid="product-details-title">
							{styleDescription}, {speciesDescription}, {finishDescription}
						</ProductDetailsTitle>
						<ProductDetailsSubtitle data-testid="product-details-subtitle">
							{shapeDescription}, {constructionDescription}
						</ProductDetailsSubtitle>
					</ProductDetails>
					{configuration?.lineItems?.map((item, index) => (
						<ReviewTruckLineItemCard
							key={item.id}
							lineItemNumber={index + 1}
							item={item}
							style={styleDescription}
							finish={finishDescription}
						/>
					))}
					<EstimatedSubTotal>
						<EstimatedSubTotalLabel data-testid="estimated-subtotal-label">
							{EstimatedSubTotalText}
						</EstimatedSubTotalLabel>
						<EstimatedSubTotalValue data-testid="estimated-subtotal-value">
							{formatPrice(configuration?.subTotal) ?? UnavailableDataPlaceholderText}
						</EstimatedSubTotalValue>
					</EstimatedSubTotal>
					<EstimatedSubTotalBanner>
						<EstimatedSubTotalBannerContainer>
							<EstimatedSubTotalBannerTextContainer>
								<EstimatedSubTotalBannerLabel>{EstimatedSubTotalText}</EstimatedSubTotalBannerLabel>
								<EstimatedSubTotalBannerValue>
									{formatPrice(configuration?.subTotal) ?? UnavailableDataPlaceholderText}
								</EstimatedSubTotalBannerValue>
							</EstimatedSubTotalBannerTextContainer>
							<EstimatedSubTotalBannerNote>
								{NewOrderViewPriceEstimatePromotionalPricingText}
							</EstimatedSubTotalBannerNote>
						</EstimatedSubTotalBannerContainer>
					</EstimatedSubTotalBanner>
				</AccordionDetailsWrapper>
			</AccordionWrapper>
		</OrderCardWrapper>
	);
};

export default ReviewTruckStopOrderCard;
